import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import { CircleUser } from "lucide-react";

const Profile = () => {
  const { user, setUser, accessToken, logout, language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const popupCloseRef = useRef(null);

  const translations = {
    el: {
      title: "Ρυθμίσεις προφίλ",
      notificationsPrompt: "Επιθυμείτε να λαμβάνετε ειδοποιήσεις;",
      username: "Όνομα χρήστη",
      email: "Email",
      firstName: "Όνομα",
      lastName: "Επώνυμο",
      saveChanges: "Αποθήκευση αλλαγών",
      saving: "Αποθήκευση...",
      deleteAccount: "Διαγραφή λογαριασμού",
      deleteConfirm: "Θέλετε να διαγράψετε οριστικά τον λογαριασμό σας;",
      noChanges: "Δεν υπάρχουν αλλαγές προς αποθήκευση",
      updateFailed: "Η ενημέρωση του προφίλ απέτυχε",
      notifUpdateError: "Αδυναμία ενημέρωσης ρυθμίσεων ειδοποιήσεων",
      profileUpdated: "Το προφίλ ενημερώθηκε",
      successMsg: "Οι αλλαγές σας αποθηκεύτηκαν με επιτυχία.",
      ok: "Εντάξει",
      discardChanges: "Αναίρεση αλλαγών",
      deleteTitle: "Διαγραφή λογαριασμού",
      cancel: "Ακύρωση",
      delete: "Διαγραφή"
    },
    en: {
      title: "Profile settings",
      notificationsPrompt: "Do you wish to receive notifications?",
      username: "Username",
      email: "Email",
      firstName: "First name",
      lastName: "Last name",
      saveChanges: "Save changes",
      saving: "Saving...",
      deleteAccount: "Delete account",
      deleteConfirm: "Delete your account permanently?",
      noChanges: "No changes to save",
      updateFailed: "Failed to update profile",
      notifUpdateError: "Could not update notification settings",
      profileUpdated: "Profile updated",
      successMsg: "Your changes have been saved successfully.",
      ok: "OK",
      discardChanges: "Discard changes",
      deleteTitle: "Delete account",
      cancel: "Cancel",
      delete: "Delete"
    }
  };

  const t = translations[language] || translations.el;

  const [formData, setFormData] = useState({
    username: "",
    full_name: "",
    last_name: "",
    email: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSavePopup, setShowSavePopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      console.log("User data from context:", user);
      setFormData({
        username: user.username || "",
        full_name: user.full_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
      });
      setNotificationsEnabled(
        user.notifications_enabled ?? true
      );
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleReset = () => {
    setFormData({
      username: user.username || "",
      full_name: user.full_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
    });
    setError("");
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    try {
      const changed = {};
      Object.keys(formData).forEach((k) => {
        if ((formData[k] || "").trim() !== (user[k] || "").trim()) {
          changed[k] = formData[k];
        }
      });

      if (!Object.keys(changed).length) {
        setError(t.noChanges);
        return;
      }

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/users/${user.user_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(changed),
        }
      );

      const data = await res.json();
      if (res.ok) {
        setUser(data.user);
        setShowSavePopup(true);
      } else {
        setError(t.updateFailed);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/users/${user.user_id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (res.ok) {
        await logout();
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  const updateNotifications = async (enabled) => {
    setNotificationsEnabled(enabled);
    setError("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/users/${user.user_id}/notifications`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ enabled }),
        }
      );

      if (!res.ok) throw new Error("Failed to update");

      const data = await res.json();

      //Update AuthContext to persist the notification preference change in the app
      setUser((prev) => ({
        ...prev,
        notifications_enabled: data.notifications_enabled,
      }));
    } catch (err) {
      // Rollback to previous state if the API call fails
      setNotificationsEnabled(!enabled);
      setError(t.notifUpdateError);
    }
  };

  // Focus the close button when the popup opens; Escape dismisses it
  useEffect(() => {
    if (!showSavePopup) return;
    if (popupCloseRef.current) popupCloseRef.current.focus();
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setShowSavePopup(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showSavePopup]);

   return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />

      <div className="flex-1 flex flex-col md:flex-row relative bg-custom-background w-full">
        <ProfileSidebar />

        <main id="profile-main" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 py-10 xl:ml-20 text-center md:text-left">
          <div id="profile-content" className="w-full max-w-2xl bg-custom-header rounded-3xl p-6 sm:p-10 shadow-xl mt-12 md:mt-0"
            style={{ height: 'auto' }}>
            <h2 className="text-2xl font-bold text-custom-letters mb-8 text-center flex items-center justify-center gap-2">
              <CircleUser className="text-custom-grad1" /> {t.title}
            </h2>


            {error && (
              <div className="mb-4 bg-red-100 text-red-700 p-3 rounded-xl text-sm text-center">
                {error}
              </div>
            )}

            <label className="flex items-center gap-3 mb-6">
              <span className="text-lg font-semibold text-custom-letters">
                {t.notificationsPrompt}
              </span>
              <input
                type="checkbox"
                checked={notificationsEnabled}
                onChange={(e) => updateNotifications(e.target.checked)}
                className="accent-custom-grad2 w-5 h-5 cursor-pointer"
              />
            </label>


            <div className="flex flex-col gap-5">
              <div>
                <label className="text-md font-semibold text-custom-letters">
                  {t.username}
                </label>
                <input
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="w-full mt-1 px-4 py-2 rounded-full 
                 bg-custom-background text-custom-letters
                 border border-custom-letters/30
                 focus:outline-none focus:ring-2 focus:ring-custom-grad2"
                />
              </div>

              <div>
                <label className="text-md font-semibold text-custom-letters">
                  {t.email}
                </label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  readOnly
                  className="w-full mt-1 px-4 py-2 rounded-full 
                 bg-custom-background text-custom-letters opacity-60 cursor-not-allowed
                 border border-custom-letters/30"
                />
              </div>

              <div>
                <label className="text-md font-semibold text-custom-letters">
                  {t.firstName}
                </label>
                <input
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full mt-1 px-4 py-2 rounded-full 
                 bg-custom-background text-custom-letters
                 border border-custom-letters/30
                 focus:outline-none focus:ring-2 focus:ring-custom-grad2"
                />
              </div>

              <div>
                <label className="text-md font-semibold text-custom-letters">
                  {t.lastName}
                </label>
                <input
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="w-full mt-1 px-4 py-2 rounded-full 
                 bg-custom-background text-custom-letters
                 border border-custom-letters/30
                 focus:outline-none focus:ring-2 focus:ring-custom-grad2"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
              <button
                onClick={handleSave}
                disabled={loading}
                className="w-full sm:w-auto px-8 py-3 rounded-full font-bold bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white"
              >
                {loading ? t.saving : t.saveChanges}
              </button>

              <button
                onClick={handleReset}
                disabled={loading}
                className="w-full sm:w-auto px-8 py-3 rounded-full font-bold border-2 border-custom-letters text-custom-letters bg-transparent hover:bg-custom-letters/10 transition"
              >
                {t.discardChanges}
              </button>

              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading}
                className="w-full sm:w-auto px-8 py-3 rounded-full font-bold bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white"
              >
                {t.deleteAccount}
              </button>
            </div>
          </div>
        </main>
      </div>

      {showSavePopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div role="dialog" aria-modal="true" aria-labelledby="save-popup-title" className="bg-custom-header rounded-2xl p-6 w-80 text-center">
            <h3 id="save-popup-title" className="text-lg font-semibold text-custom-letters mb-3">
              {t.profileUpdated}
            </h3>
            <p className="text-sm text-custom-letters mb-6">
              {t.successMsg}
            </p>
            <button
              onClick={() => setShowSavePopup(false)}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white"
            >
              {t.ok}
            </button>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div role="dialog" aria-modal="true" aria-labelledby="profile-delete-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
            <h3 id="profile-delete-title" className="text-lg font-semibold text-custom-letters mb-3">{t.deleteTitle}</h3>
            <p className="text-sm text-custom-letters mb-6">{t.deleteConfirm}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-6 py-2 rounded-full border-2 border-custom-letters text-custom-letters font-semibold hover:bg-custom-letters/10 transition">
                {t.cancel}
              </button>
              <button
                onClick={handleDelete}
                className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white font-bold">
                {t.delete}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
